import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { IPerson, Person } from '@shared/models/person';
import { BcmService } from '@modules/bcm/bcm.service';
import { BcmBaseFacade } from '../bcm-base.facade';
import { ConfirmDialogService } from '@sharedComponents/dialogs/confirm-dialog/confirm-dialog.service';
import { BcmUserPermission } from '@modules/bcm/bcm-user-permission';
import { AppNotificationService } from '@core/services/app-notification.service';
import { personsTableData } from '@modules/bcm/@core/state-management/persons/persons-table-data';
import { BcmNavigationService } from '@modules/bcm/bcm-navigation.service';
import { ThemePalette } from '@angular/material/core';
import { ImageCropperDialogService } from '@sharedComponents/dialogs/image-cropper-dialog/image-cropper-dialog.service';
import { EMPTY, forkJoin, of, switchMap } from 'rxjs';
import { FilesApiService, PersonApiService } from '@modules/bcm/@shared/services';
import { BcmUserSettingsFacade } from '@bcmServices/settings/bcm-user-settings-facade';
import { filteredListExportHeaderActionBase } from '@modules/bcm/@core/state-management/filtered-list-export-header.util';
import {
    GetTenantRelationDataDialogComponent
} from '@sharedComponents/dialogs/get-tenant-relation-data/get-tenant-relation-data-dialog.component';
import { TenantRelationAssignment } from '@shared/models/tenant-relation-assignment';
import { MatDialog } from '@angular/material/dialog';
import { tap } from 'rxjs/operators';
import { InvoicePosition } from '@shared/models/invoice-position';
import { BcmInvoicePositionsService } from '@bcmServices/invoice-positions.service';
import {
    GetCountryDialogComponent
} from '@modules/bcm/@shared/components/dialogs/get-country-dialog/get-country-dialog.component';
import { Country } from '@shared/models/country';
import { BcmBaseBulkChanges } from '@modules/bcm/@core/state-management/bcm-base-bulk-changes';
import { TolgeeService } from '@core/translation/tolgee.service';

@Injectable({providedIn: 'root'})
export class PersonsFacade extends BcmBaseFacade<Person, IPerson> {

    readonly resourcePath = 'persons';

    protected override readonly cacheListPages = true;

    protected override get listResourcePath(): string {
        return 'persons/index';
    }

    get resourceNameSingular(): string {
        return TolgeeService.translate('person.singular');
    }

    get resourceNamePlural(): string {
        return TolgeeService.translate('person.plural');
    }

    readonly resourceIconPlural = 'person';

    readonly resourceIconSingular = 'person';

    readonly deletePermission = BcmUserPermission.PERSONS_DELETE;

    readonly readPermission = BcmUserPermission.PERSONS_READ;

    readonly writePermission = BcmUserPermission.PERSONS_WRITE;

    readonly hideGenericExcelButton = true;

    readonly headerActions = [
        {
            titleKey: 'table.exportCurrentView',
            icon: 'table_view',
            onClick: () => this.exportTableAsExcel(),
            ...filteredListExportHeaderActionBase(),
        },
    ];

    readonly rowActions = [
        {
            titleKey: 'table.rowActionView',
            icon: 'assignment',
            iconColor: 'accent' as ThemePalette,
            onClick: (person: Person) => this._bcmNavigationService.navigate('persons/' + person.id),
        },
        {
            titleKey: 'table.rowActionDelete',
            icon: 'delete',
            iconColor: 'warn' as ThemePalette,
            onClick: (person: Person) => super.remove(person, person.fullName).subscribe(),
        }
    ];

    readonly bulkChanges = new BcmBaseBulkChanges<Person>()
        .setButtons(
            {
                titleKey: 'table.bulkTenantRelation',
                icon: 'add',
                iconColor: 'accent' as ThemePalette,
                resetSelectionAfterSuccess: true,
                onClick: (persons: Person[]) => this.addTenantRelation(...persons),
            },
            {
                titleKey: 'table.bulkInvoicePositionPerson',
                icon: 'add',
                iconColor: 'accent' as ThemePalette,
                resetSelectionAfterSuccess: true,
                onClick: (persons: Person[]) => this.addInvoicePosition(...persons),
            },
            {
                titleKey: 'table.bulkCountry',
                icon: 'edit',
                iconColor: 'accent' as ThemePalette,
                resetSelectionAfterSuccess: true,
                onClick: (persons: Person[]) => this.updateCountry(...persons),
            },
            {
                titleKey: 'common.delete',
                icon: 'delete',
                iconColor: 'warn' as ThemePalette,
                resetSelectionAfterSuccess: true,
                onClick: (persons: Person[]) => super.removeMultiple(persons),
            },
        );

    constructor(appNotificationService: AppNotificationService,
                confirmDialogService: ConfirmDialogService,
                bcmUserSettingsFacade: BcmUserSettingsFacade,
                httpClient: HttpClient,
                bcmService: BcmService,
                filesApiService: FilesApiService,
                bcmNavigationService: BcmNavigationService,
                private _bcmNavigationService: BcmNavigationService,
                private _imageCropperDialogService: ImageCropperDialogService,
                private _personApiService: PersonApiService,
                private _bcmInvoicePositionsService: BcmInvoicePositionsService,
                private _matDialog: MatDialog,
    ) {
        super(
            Person,
            personsTableData,
            bcmNavigationService,
            appNotificationService,
            confirmDialogService,
            bcmUserSettingsFacade,
            httpClient,
            bcmService,
            filesApiService
        );
    }

    public changeImageFrom(person: Person) {
        this._imageCropperDialogService
            .getCroppedImage(person.image)
            .pipe(switchMap(newImage => {
                if (newImage) {
                    return this.addOrUpdateFile(newImage);
                } else {
                    return EMPTY;
                }
            }))
            .subscribe(fileFromBackEnd => {
                if (fileFromBackEnd) {
                    person.image = fileFromBackEnd;
                    this.updateStateFor(person, true);
                }
            });
    }

    public deleteImageFrom(person: Person) {
        super.deleteFile(person.image)
            .subscribe(deleted => {
                if (deleted) {
                    person.image = undefined;
                    this.updateStateFor(person, true);
                }
            });
    }

    addTenantRelation(...persons: Person[]) {
        const person = persons.length === 1 ? persons[0] : undefined;
        const dialogRef = this._matDialog.open(GetTenantRelationDataDialogComponent, {data: {person}});

        return dialogRef
            .afterClosed()
            .pipe(
                switchMap((tenantRelationAssignment: TenantRelationAssignment) => {

                    if (!tenantRelationAssignment) {
                        return of(undefined);
                    }

                    return this._personApiService
                        .multipleNewTenantRelationAssignment(tenantRelationAssignment, ...persons)
                        .pipe(tap((tenantRelationAssignments) => {
                            persons.forEach(p => p.addTenantRelationAssignment(
                                tenantRelationAssignments.find(assignment => assignment.person?.id === p.id)
                            ));
                            this.appNotificationService.showSuccess(TolgeeService.translate('bcmTenantRelations.notifications.relationsCreated'));
                        }));
                })
            );
    }

    addInvoicePosition(...persons: Person[]) {
        return this._bcmInvoicePositionsService
            .addPositionsForMultiplePersons(persons)
            .pipe(tap((positions: InvoicePosition[]) => {
                if (positions.length > 0) {
                    this.appNotificationService.showSuccess(TolgeeService.translate('person.invoices.positions.notifications.added'));
                }
            }));
    }

    updateCountry(...persons: Person[]) {
        const dialogRef = this._matDialog.open(GetCountryDialogComponent);

        return dialogRef
            .afterClosed()
            .pipe(
                switchMap((country: Country) => {
                    if (country) {
                        persons.forEach(person => person.country = country);
                        return forkJoin(
                            persons.map(person => super.update(person, {country} as Partial<Person>))
                        );
                    }
                    return EMPTY;
                })
            );
    }
}
